import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, LeafSprig } from "@/components/Motion";
import { Marquee } from "@/components/Marquee";
import { Packages } from "@/components/Packages";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CHAPTERS, CONTACT, FOOTPRINT, IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const Folio = ({ section, num, tone = "dark" }) => (
  <div
    className={`mx-auto mt-24 flex max-w-7xl items-center gap-6 px-5 sm:px-8 ${tone === "light" ? "text-ink/45" : "text-sage/45"}`}
    aria-hidden="true"
  >
    <span className={`h-px flex-1 ${tone === "light" ? "bg-gold-deep/40" : "bg-gold/25"}`} />
    <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.3em]">
      Root2Shoot&nbsp;&nbsp;•&nbsp;&nbsp;{section}&nbsp;&nbsp;•&nbsp;&nbsp;{num}
    </span>
    <span className={`h-px flex-1 ${tone === "light" ? "bg-gold-deep/40" : "bg-gold/25"}`} />
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-noir" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.cafeStorefront}
          alt="The Root2Shoot Sustainable Café in Stockport — shopfront and interior with plants, wooden tables and the counter"
          className="h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/60 to-noir/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-noir/70 via-noir/20 to-noir/35" />
        <div className="absolute inset-0 bg-noir/20" />
      </motion.div>
      <LeafSprig className="pointer-events-none absolute -right-8 top-20 z-10 h-80 rotate-[160deg] text-cream/10" />
      <LeafSprig className="pointer-events-none absolute -left-8 bottom-10 z-10 h-64 -rotate-12 text-cream/10" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-44 sm:px-8">
        <motion.p
          className="label-mono flex items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Sustainable Café · Stockport <span className="text-gold/60" aria-hidden="true">/</span> Sustainable Film &amp; TV Catering
          <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
        </motion.p>
        <MaskedLines
          lines={["Good food fuels", "great stories"]}
          lineClassName={["text-cream", "text-gold"]}
          className="mt-8 font-editorial text-[13vw] leading-[0.98] tracking-tight sm:text-8xl lg:text-9xl"
        />
        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <motion.p
            className="max-w-md text-base leading-relaxed text-cream/85 sm:text-lg lg:col-span-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          >
            Root2Shoot is a sustainable café and external production catering service. We prepare
            great food from our permanent kitchen and deliver it fresh, hotboxed and ready to serve.
          </motion.p>
          <motion.div
            className="flex flex-wrap gap-4 lg:col-span-6 lg:justify-end"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: EASE }}
          >
            <Link to="/cafe" className="btn-gold" data-testid="hero-cta-cafe">
              Visit the Café
            </Link>
            <Link to="/catering" className="btn-ghost" data-testid="hero-cta-catering">
              Film &amp; TV Catering
            </Link>
          </motion.div>
        </div>
        <motion.p
          className="mt-10 border-t border-gold/25 pt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-cream/70 sm:text-[11px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
          data-testid="hero-cafe-hours"
        >
          The Café · Open to the public · Mon–Fri 8:00–15:00 · Studio House, Stockport
        </motion.p>
      </div>
    </section>
  );
};

const TwoSides = () => (
  <section className="relative overflow-hidden bg-obsidian pb-28 pt-24 sm:pb-36" data-testid="dual-brand-section">
    <LeafSprig className="pointer-events-none absolute -left-10 top-0 h-80 -rotate-12 text-leaf-light/10" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="label-mono flex items-center gap-4">
          One Kitchen, Two Worlds
          <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
        </p>
        <h2 className="mt-6 max-w-3xl font-editorial text-4xl leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
          Choose your side of <span className="text-gold">the story</span>
        </h2>
      </Reveal>

      <article className="mt-20 grid items-center gap-10 lg:mt-28 lg:grid-cols-12" data-testid="anchor-cafe-card">
        <Reveal className="relative lg:col-span-7">
          <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
          <img
            src={IMAGES.cafeStorefront}
            alt="Inside the Root2Shoot Sustainable Café in Stockport — plants, wooden tables and the counter"
            className="relative aspect-[4/3] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="spread-cafe-image"
          />
        </Reveal>
        <Reveal delay={0.12} className="relative z-10 lg:col-span-5 lg:-ml-24">
          <div className="border border-gold/30 bg-obsidian/95 p-8 sm:p-10">
            <img
              src="/images/logo-cafe-banner.png"
              alt="Root2Shoot Sustainable Café"
              className="mb-7 h-12 w-auto sm:h-14"
              loading="lazy"
              decoding="async"
            />
            <p className="label-mono">01 — Sustainable Café · Open to the Public</p>
            <h3 className="mt-5 font-editorial text-4xl tracking-tight text-cream sm:text-5xl">The Café</h3>
            <p className="mt-3 font-display text-2xl italic text-gold">Good food for ordinary days.</p>
            <p className="mt-5 text-base leading-relaxed text-sage">
              A sustainable café serving great food, excellent coffee and thoughtful everyday dishes.
              Open to the public, Monday to Friday — morning bakes, all-day breakfasts and bean-to-cup
              coffee in a space built for neighbours, freelancers and the odd film crew planning their day.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link to="/cafe" className="btn-gold" data-testid="home-cafe-cta">
                Visit the Café
              </Link>
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="cursor-not-allowed font-mono text-[10px] uppercase tracking-[0.25em] text-sage/50"
                data-testid="ordering-coming-soon-btn"
              >
                Online Ordering — Launching Soon
              </button>
            </div>
          </div>
        </Reveal>
      </article>

      <article className="mt-24 grid items-center gap-10 lg:mt-36 lg:grid-cols-12" data-testid="anchor-catering-card">
        <Reveal delay={0.12} className="relative z-10 lg:col-span-5">
          <img
            src="/images/logo-filmtv-banner.png"
            alt="Root2Shoot Sustainable Film and TV Catering"
            className="mb-7 h-12 w-auto sm:h-14"
            loading="lazy"
            decoding="async"
          />
          <p className="label-mono">02 — Sustainable Film &amp; TV Catering</p>
          <h3 className="mt-5 font-editorial text-4xl tracking-tight text-cream sm:text-5xl">
            Film &amp; TV <span className="text-gold">Catering</span>
          </h3>
          <p className="mt-3 font-display text-2xl italic text-gold">Food that works around production.</p>
          <p className="mt-5 max-w-md text-base leading-relaxed text-sage">
            External production catering prepared from our permanent kitchen and delivered ready to
            serve — individually boxed, hotboxed and delivered hot, for crews of ten to five hundred,
            anywhere the shoot goes.
          </p>
          <Link to="/catering" className="btn-ghost mt-8" data-testid="catering-strip-cta">
            View Catering
          </Link>
        </Reveal>
        <Reveal delay={0.12} className="relative lg:col-span-7">
          <div className="lg:mr-[calc((80rem-100vw)/2)]">
            <div className="absolute -inset-0 -translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
            <img
              src={IMAGES.hotboxGrid}
              alt="A lineup of branded Root2Shoot hotboxes ready for delivery to a film or TV production"
              className="relative aspect-[16/11] w-full object-cover"
              loading="lazy"
              decoding="async"
              data-testid="spread-catering-image"
            />
          </div>
        </Reveal>
      </article>
    </div>
  </section>
);

const Production = () => (
  <section className="relative overflow-hidden bg-noir py-28 sm:py-36" data-testid="production-section">
    <LeafSprig className="pointer-events-none absolute -right-8 top-10 h-72 rotate-[160deg] text-cream/10" />
    <LeafSprig className="pointer-events-none absolute -left-8 bottom-10 h-72 -rotate-12 text-cream/10" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <p className="label-mono flex items-center gap-4">
            The Root2Shoot Approach
            <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
          </p>
          <h2 className="mt-6 font-editorial text-4xl leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            Food that works
            <br />
            <span className="text-gold">for production</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-sage sm:text-lg">
            Good food fuels great stories. Our service is built around the rhythm of film and
            television production, from early call times to location moves and long days.
          </p>
        </Reveal>
      </div>

      <article className="mt-24 grid items-center gap-10 lg:mt-32 lg:grid-cols-12" data-testid="production-moment-1">
        <Reveal className="lg:col-span-6">
          <p className="font-editorial text-7xl leading-none text-gold sm:text-8xl">01</p>
          <h3 className="mt-6 font-editorial text-3xl tracking-tight text-cream sm:text-5xl">
            Every diet, <span className="text-gold">one table</span>
          </h3>
          <p className="mt-5 max-w-md text-base leading-relaxed text-sage sm:text-lg">
            Vegan, gluten-free, halal and allergy requirements are catered for thoughtfully, without
            creating a "special plate". Everyone eats well, together.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="relative lg:col-span-5 lg:col-start-8">
          <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
          <img
            src={IMAGES.hotboxChicken}
            alt="A Root2Shoot hotbox with grilled chicken, asparagus and pickled onions — full dietary coverage for the whole crew"
            className="relative aspect-[4/5] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="production-image-1"
          />
        </Reveal>
      </article>

      <article className="mt-24 border-t border-gold/15 pt-20 lg:mt-32" data-testid="production-moment-2">
        <div className="grid items-baseline gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-3">
            <p className="font-editorial text-7xl leading-none text-gold sm:text-9xl">02</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <h3 className="font-editorial text-3xl tracking-tight text-cream sm:text-5xl">
              Every package, <span className="text-gold">hotboxed</span>
            </h3>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-sage sm:text-lg">
              Every package includes hotboxing and disposables. It is a complete price, so you know
              exactly what is included.
            </p>
          </Reveal>
        </div>
      </article>

      <article className="mt-24 border-t border-gold/15 pt-20 lg:mt-32" data-testid="production-moment-3">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">03</p>
            <h3 className="mt-6 max-w-3xl font-editorial text-4xl leading-[1.05] tracking-tight text-cream sm:text-6xl">
              We bring your food, <span className="text-gold">not the kitchen</span>
            </h3>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-sage sm:text-lg">
              Food is prepared at our permanent kitchen, hotboxed and delivered ready to serve.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="relative lg:col-span-3 lg:col-start-10">
            <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
            <img
              src={IMAGES.hotboxRamen}
              alt="A Root2Shoot hotbox of crispy pork belly ramen with pak choi, lime and soft egg"
              className="relative aspect-square w-full object-cover"
              loading="lazy"
              decoding="async"
              data-testid="production-image-3"
            />
          </Reveal>
        </div>
      </article>
    </div>
    <Folio section="The Approach" num="02" />
  </section>
);

const Manifesto = () => (
  <section className="relative overflow-hidden bg-obsidian py-28 sm:py-36" data-testid="manifesto-section">
    <LeafSprig className="pointer-events-none absolute -right-10 top-8 h-80 rotate-[150deg] text-leaf-light/12" />
    <LeafSprig className="pointer-events-none absolute -left-10 bottom-8 h-80 -rotate-[15deg] text-leaf-light/12" />
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <p className="label-mono flex items-center gap-4">
          Three Chapters We Cook By
          <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
        </p>
        <h2 className="mt-6 font-editorial text-5xl leading-[1.0] tracking-tight text-cream sm:text-6xl lg:text-7xl">
          The Root2Shoot
          <br />
          <span className="text-gold">manifesto</span>
        </h2>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Great food is about more than what's on the plate. It's about people, the planet and a
          smoother, brighter future for the industry we love.
        </p>
      </Reveal>

      <div className="mt-20">
        {CHAPTERS.map((ch) => (
          <Reveal key={ch.number}>
            <div
              className="grid gap-6 border-t border-gold/20 py-12 sm:grid-cols-[110px_1px_1fr] sm:gap-10 sm:py-14"
              data-testid={`manifesto-chapter-${ch.number}`}
            >
              <span className="font-editorial text-6xl leading-none text-gold sm:text-7xl">{ch.number}</span>
              <span className="hidden w-px bg-gold/40 sm:block" aria-hidden="true" />
              <div>
                <h3 className="font-editorial text-2xl tracking-tight text-cream sm:text-4xl">{ch.title}</h3>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-sage">{ch.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p
          className="mt-14 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-gold/70"
          data-testid="manifesto-footer-line"
        >
          People&nbsp;&nbsp;|&nbsp;&nbsp;Great Food&nbsp;&nbsp;|&nbsp;&nbsp;Lower Impact&nbsp;&nbsp;|&nbsp;&nbsp;Brighter Tomorrows
        </p>
      </Reveal>
    </div>
    <Folio section="The Manifesto" num="03" />
  </section>
);

const Footprint = () => (
  <section className="relative overflow-hidden bg-noir py-28 sm:py-36" data-testid="footprint-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="text-center">
        <p className="label-mono flex items-center justify-center gap-4">
          <span className="h-px w-14 bg-gold/50" aria-hidden="true" />
          Sustainability in Everything We Do
          <span className="h-px w-14 bg-gold/50" aria-hidden="true" />
        </p>
        <h2 className="mt-6 font-editorial text-4xl leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl">
          A lighter footprint,
          <br />
          <span className="font-display italic text-gold">built in from the start</span>
        </h2>
        <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Kaytie and Kev build sustainability into menu planning from the start. The carbon impact of
          food is considered before the menu is created, alongside sourcing, seasonality, packaging,
          transport, food waste and the wider catering operation.
        </p>
      </Reveal>

      <div className="mt-20 grid items-center gap-14 lg:mt-28 lg:grid-cols-12 lg:gap-8">
        <div className="order-2 grid gap-14 sm:grid-cols-2 lg:order-1 lg:col-span-3 lg:grid-cols-1 lg:text-right">
          {[FOOTPRINT[0], FOOTPRINT[2]].map((f) => (
            <Reveal key={f.id}>
              <div data-testid={`footprint-${f.id}`}>
                <p className="label-mono">{f.title}</p>
                <span className="mt-4 block h-px w-10 bg-gold/50 lg:ml-auto" aria-hidden="true" />
                <p className="mt-4 text-sm leading-relaxed text-sage sm:text-base">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="relative order-1 lg:order-2 lg:col-span-6">
          <LeafSprig className="pointer-events-none absolute -left-10 -top-10 z-10 h-56 -rotate-12 text-leaf-light/25" />
          <LeafSprig className="pointer-events-none absolute -bottom-10 -right-10 z-10 h-56 rotate-[160deg] text-leaf-light/25" />
          <img
            src={IMAGES.fruitPlatter}
            alt="A fresh fruit platter prepared in the Root2Shoot kitchen — seasonal, colourful and plant-forward"
            className="w-full object-cover aspect-[4/5] sm:aspect-[5/5] lg:scale-105"
            loading="lazy"
            decoding="async"
            data-testid="footprint-image"
          />
        </Reveal>
        <div className="order-3 grid gap-14 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-1">
          {[FOOTPRINT[1], FOOTPRINT[3]].map((f) => (
            <Reveal key={f.id} delay={0.15}>
              <div data-testid={`footprint-${f.id}`}>
                <p className="label-mono">{f.title}</p>
                <span className="mt-4 block h-px w-10 bg-gold/50" aria-hidden="true" />
                <p className="mt-4 text-sm leading-relaxed text-sage sm:text-base">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal className="mt-24">
        <div
          className="grid items-center gap-6 border border-gold/40 px-8 py-9 sm:grid-cols-[auto_1fr_auto] sm:px-12"
          data-testid="bafta-note"
        >
          <p className="label-mono">BAFTA ALBERT Principles</p>
          <p className="text-base leading-relaxed text-cream/85">
            Our approach follows the principles of BAFTA ALBERT and the wider sustainability
            direction of the film and television industry.
          </p>
          <Link to="/sustainability" className="link-more" data-testid="footprint-more-link">
            How We Operate
          </Link>
        </div>
      </Reveal>
    </div>
    <Folio section="A Lighter Footprint" num="04" />
  </section>
);

const Quote = ({ presetTier }) => (
  <section id="quote" className="relative overflow-hidden bg-obsidian py-28 sm:py-36" data-testid="home-quote-section">
    <LeafSprig className="pointer-events-none absolute -left-10 top-10 h-80 -rotate-12 text-leaf-light/10" />
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="label-mono flex items-center gap-4">
            Get a Quote
            <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
          </p>
          <h2 className="mt-6 font-editorial text-4xl leading-[1.02] tracking-tight text-cream sm:text-6xl">
            Tell us what the
            <br />
            <span className="text-gold">production needs</span>
          </h2>
          <p className="mt-8 text-base leading-relaxed text-sage sm:text-lg">
            Not every production fits neatly into three boxes. That's absolutely fine. Choose a
            package as a starting point, or tell us your budget, crew numbers, schedule, location and
            requirements and we'll quote around the production.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#quote-form" className="btn-gold" data-testid="home-quote-cta">
              Get a Quote
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="home-quote-contact-cta">
              Contact Us
            </Link>
          </div>
          <p className="mt-12 border-t border-gold/20 pt-7 text-sm leading-relaxed text-sage">
            Prefer to talk?{" "}
            <a href="tel:+447413027418" className="text-gold underline-offset-4 hover:underline" data-testid="home-quote-phone">
              07413 027418
            </a>{" "}
            — also on WhatsApp. Or email{" "}
            <a href="mailto:info@root2shoot.com" className="text-gold underline-offset-4 hover:underline" data-testid="home-quote-email">
              info@root2shoot.com
            </a>
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="lg:col-span-7">
        <div id="quote-form" className="scroll-mt-28 border border-gold/25 bg-noir/70 p-7 sm:p-12">
        <p className="label-mono mb-10">Catering Enquiry</p>
          <EnquiryForm variant="catering" presetTier={presetTier} />
        </div>
      </Reveal>
    </div>
  </section>
);

const BackCover = () => (
  <section className="relative overflow-hidden bg-noir" data-testid="brand-statement">
    <img
      src={IMAGES.botanicalFern}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 h-full w-full object-cover opacity-25"
      loading="lazy"
      decoding="async"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-noir via-noir/70 to-noir" />
    <div className="relative mx-auto flex max-w-5xl flex-col items-center px-5 py-32 text-center sm:px-8 sm:py-44">
      <img
        src="/images/logo-duo.png"
        alt="Root2Shoot — Sustainable Café and Film & TV Catering"
        className="h-20 w-auto sm:h-28"
        loading="lazy"
        decoding="async"
      />
      <Reveal>
        <p className="mt-14 font-editorial text-5xl leading-[1.05] tracking-tight text-cream sm:text-7xl lg:text-8xl">
          We bring your food,
          <br />
          <span className="text-gold">not the kitchen.</span>
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-14 font-mono text-[11px] uppercase tracking-[0.3em] text-gold/70">
          Good Food&nbsp;&nbsp;|&nbsp;&nbsp;Lower Impact&nbsp;&nbsp;|&nbsp;&nbsp;Brighter Tomorrows
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const [presetTier, setPresetTier] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <>
      <Seo
        title="Root2Shoot — Sustainable Café & Film & TV Production Catering, Stockport"
        siteName="Root2Shoot"
        description="Root2Shoot is a sustainable café open to the public in Stockport, and a sustainable film & TV catering service — plant-forward hotbox catering for film and TV crews, delivered fresh and ready to serve across the UK."
        image="/images/logo-duo.png"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Root2Shoot",
            url: origin,
            logo: `${origin}/images/logo-duo.png`,
            email: CONTACT.email,
            telephone: CONTACT.phoneInternational,
            slogan: "Good Food — Brighter Futures",
            address: {
              "@type": "PostalAddress",
              streetAddress: CONTACT.street,
              addressLocality: CONTACT.locality,
              addressRegion: CONTACT.region,
              postalCode: CONTACT.postcode,
              addressCountry: "GB",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Root2Shoot",
            url: origin,
          },
          {
            "@context": "https://schema.org",
            "@type": "FoodEstablishment",
            name: "Root2Shoot",
            description: "Sustainable cafe and sustainable film & TV catering business.",
            slogan: "Good Food — Brighter Futures",
            email: CONTACT.email,
            telephone: CONTACT.phoneInternational,
            address: {
              "@type": "PostalAddress",
              streetAddress: CONTACT.street,
              addressLocality: CONTACT.locality,
              addressRegion: CONTACT.region,
              postalCode: CONTACT.postcode,
              addressCountry: "GB",
            },
          },
        ]}
      />
      <Hero />
      <Marquee />
      <TwoSides />
      <Production />
      <Manifesto />
      <Footprint />
      <Packages onEnquire={setPresetTier} enquiryHref="#quote-form" />
      <Quote presetTier={presetTier} />
      <BackCover />
    </>
  );
}
